import { useEffect, useRef, useState } from 'react';
import { Map as MaplibreMap, GeoJSONSource, LayerSpecification, MapSourceDataEvent } from 'maplibre-gl'; // Aggiunto MapSourceDataEvent
import type { LayerBase, LayerStyleProperties, GeoDataState } from '../../../store/types'; // Rimosso GeoJSONSourceSpecification
import useGeoData from '../../../store/useGeoData';
import { shallow } from 'zustand/shallow';
import type { SourceSpecificationUnion, VectorSourceSpecification, RasterSourceSpecification, RasterDemSourceSpecification, GeoJSONSourceSpecification, VideoSourceSpecification, ImageSourceSpecification } from '../../../store/types'; // Importa SourceSpecificationUnion e i tipi specifici
import { registerPmtilesInstance } from './useMapInitialization'; // Importa la funzione di registrazione

// Funzione helper per pulire gli oggetti di stile (paint/layout)
const cleanStyleObject = (styleProps?: Record<string, any>): Record<string, any> | undefined => {
  if (!styleProps) return undefined;
  const cleaned: Record<string, any> = {};
  for (const key in styleProps) {
    if (Object.prototype.hasOwnProperty.call(styleProps, key) && !key.startsWith('_')) {
      cleaned[key] = styleProps[key];
    }
  }
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

// Tipo combinato per i dati completi del layer usati internamente
type FullLayerData = LayerBase & LayerStyleProperties;

// Helper type per le proprietà Paint
type PaintProperties = { [key: string]: any };
// Helper type per le proprietà Layout
type LayoutProperties = { [key: string]: any };


// --- Funzioni Helper per costruire oggetti Paint e Layout ---
// NOTA: Queste funzioni sono state spostate qui per essere accessibili da recreateSourceAndLayers
const getPaintProperties = (currentLayerState: FullLayerData): PaintProperties => {
    const paint: PaintProperties = {};
    const props = currentLayerState;
    const type = props.maplibreLayerType;

    if (type === 'fill') {
        paint['fill-color'] = props.fillColor;
        paint['fill-opacity'] = props.fillOpacity;
        paint['fill-outline-color'] = props.lineColor; // Nota: lineColor usato per fill-outline-color
        paint['fill-antialias'] = props.fillAntialias;
        paint['fill-translate'] = props.fillTranslate;
        paint['fill-translate-anchor'] = props.fillTranslateAnchor;
        if (props.fillPattern !== null) paint['fill-pattern'] = props.fillPattern;
    } else if (type === 'line') {
        paint['line-color'] = props.lineColor;
        paint['line-opacity'] = props.lineOpacity;
        paint['line-width'] = props.lineWidth;
        paint['line-translate'] = props.lineTranslate;
        paint['line-translate-anchor'] = props.lineTranslateAnchor;
        paint['line-gap-width'] = props.lineGapWidth;
        paint['line-offset'] = props.lineOffset;
        paint['line-blur'] = props.lineBlur;
        // !! WORKAROUND !!: Escludi volutamente line-dasharray e line-pattern
    } else if (type === 'circle') {
        paint['circle-color'] = props.circleColor;
        paint['circle-opacity'] = props.circleOpacity;
        paint['circle-radius'] = props.circleRadius;
        paint['circle-stroke-color'] = props.circleStrokeColor;
        paint['circle-stroke-width'] = props.circleStrokeWidth;
        paint['circle-blur'] = props.circleBlur;
        paint['circle-translate'] = props.circleTranslate;
        paint['circle-translate-anchor'] = props.circleTranslateAnchor;
        paint['circle-pitch-scale'] = props.circlePitchScale;
        paint['circle-pitch-alignment'] = props.circlePitchAlignment;
        paint['circle-stroke-opacity'] = props.circleStrokeOpacity;
    } else if (type === 'symbol') {
        if (props.iconOpacity !== undefined) paint['icon-opacity'] = props.iconOpacity;
        if (props.iconColor !== undefined) paint['icon-color'] = props.iconColor;
        if (props.iconHaloColor !== undefined) paint['icon-halo-color'] = props.iconHaloColor;
        if (props.iconHaloWidth !== undefined) paint['icon-halo-width'] = props.iconHaloWidth;
        if (props.iconHaloBlur !== undefined) paint['icon-halo-blur'] = props.iconHaloBlur;
        if (props.iconTranslate !== undefined) paint['icon-translate'] = props.iconTranslate;
        if (props.iconTranslateAnchor !== undefined) paint['icon-translate-anchor'] = props.iconTranslateAnchor;
        if (props.textOpacity !== undefined) paint['text-opacity'] = props.textOpacity;
        if (props.textColor !== undefined) paint['text-color'] = props.textColor;
        if (props.textHaloColor !== undefined) paint['text-halo-color'] = props.textHaloColor;
        if (props.textHaloWidth !== undefined) paint['text-halo-width'] = props.textHaloWidth;
        if (props.textHaloBlur !== undefined) paint['text-halo-blur'] = props.textHaloBlur;
        if (props.textTranslate !== undefined) paint['text-translate'] = props.textTranslate;
        if (props.textTranslateAnchor !== undefined) paint['text-translate-anchor'] = props.textTranslateAnchor;
    } else if (type === 'fill-extrusion') {
        if (props.fillExtrusionOpacity !== undefined) paint['fill-extrusion-opacity'] = props.fillExtrusionOpacity;
        if (props.fillExtrusionColor !== undefined) paint['fill-extrusion-color'] = props.fillExtrusionColor;
        if (props.fillExtrusionTranslate !== undefined) paint['fill-extrusion-translate'] = props.fillExtrusionTranslate;
        if (props.fillExtrusionTranslateAnchor !== undefined) paint['fill-extrusion-translate-anchor'] = props.fillExtrusionTranslateAnchor;
        if (props.fillExtrusionPattern !== null) paint['fill-extrusion-pattern'] = props.fillExtrusionPattern;
        if (props.fillExtrusionHeight !== undefined) paint['fill-extrusion-height'] = props.fillExtrusionHeight;
        if (props.fillExtrusionBase !== undefined) paint['fill-extrusion-base'] = props.fillExtrusionBase;
        if (props.fillExtrusionVerticalGradient !== undefined) paint['fill-extrusion-vertical-gradient'] = props.fillExtrusionVerticalGradient;
    } else if (type === 'raster') { // Rimosso WMS qui
        // Proprietà Paint per Raster
        if (props.rasterOpacity !== undefined) paint['raster-opacity'] = props.rasterOpacity;
        if (props.rasterBrightnessMin !== undefined) paint['raster-brightness-min'] = props.rasterBrightnessMin;
        if (props.rasterBrightnessMax !== undefined) paint['raster-brightness-max'] = props.rasterBrightnessMax;
        if (props.rasterContrast !== undefined) paint['raster-contrast'] = props.rasterContrast;
        if (props.rasterFadeDuration !== undefined) paint['raster-fade-duration'] = props.rasterFadeDuration;
        if (props.rasterHueRotate !== undefined) paint['raster-hue-rotate'] = props.rasterHueRotate;
        if (props.rasterResampling !== undefined) paint['raster-resampling'] = props.rasterResampling;
        if (props.rasterSaturation !== undefined) paint['raster-saturation'] = props.rasterSaturation;
    } else if (type === 'heatmap') {
        if (props.heatmapColor !== undefined) paint['heatmap-color'] = props.heatmapColor;
        if (props.heatmapIntensity !== undefined) paint['heatmap-intensity'] = props.heatmapIntensity;
        if (props.heatmapOpacity !== undefined) paint['heatmap-opacity'] = props.heatmapOpacity;
        if (props.heatmapRadius !== undefined) paint['heatmap-radius'] = props.heatmapRadius;
        if (props.heatmapWeight !== undefined) paint['heatmap-weight'] = props.heatmapWeight;
    }
    return paint;
};

const getLayoutProperties = (currentLayerState: FullLayerData): LayoutProperties => {
    const layout: LayoutProperties = {};
    const props = currentLayerState;
    const type = props.maplibreLayerType;

    if (type === 'fill') {
        if (props.fillSortKey !== null) layout['fill-sort-key'] = props.fillSortKey;
    } else if (type === 'line') {
        layout['line-cap'] = props.lineCap;
        layout['line-join'] = props.lineJoin;
        layout['line-miter-limit'] = props.lineMiterLimit;
        layout['line-round-limit'] = props.lineRoundLimit;
        if (props.lineSortKey !== null) layout['line-sort-key'] = props.lineSortKey;
    } else if (type === 'circle') {
        if (props.circleSortKey !== null) layout['circle-sort-key'] = props.circleSortKey;
    } else if (type === 'symbol') {
        if (props.symbolPlacement !== undefined) layout['symbol-placement'] = props.symbolPlacement;
        if (props.symbolSpacing !== undefined) layout['symbol-spacing'] = props.symbolSpacing;
        if (props.symbolAvoidEdges !== undefined) layout['symbol-avoid-edges'] = props.symbolAvoidEdges;
        if (props.symbolSortKey !== null) layout['symbol-sort-key'] = props.symbolSortKey;
        if (props.symbolZOrder !== undefined) layout['symbol-z-order'] = props.symbolZOrder;
        if (props.iconAllowOverlap !== undefined) layout['icon-allow-overlap'] = props.iconAllowOverlap;
        if (props.iconIgnorePlacement !== undefined) layout['icon-ignore-placement'] = props.iconIgnorePlacement;
        if (props.iconOptional !== undefined) layout['icon-optional'] = props.iconOptional;
        if (props.iconRotationAlignment !== undefined) layout['icon-rotation-alignment'] = props.iconRotationAlignment;
        if (props.iconSize !== undefined) layout['icon-size'] = props.iconSize;
        if (props.iconTextFit !== undefined) layout['icon-text-fit'] = props.iconTextFit;
        if (props.iconTextFitPadding !== undefined) layout['icon-text-fit-padding'] = props.iconTextFitPadding;
        if (props.iconImage !== null) layout['icon-image'] = props.iconImage;
        if (props.iconRotate !== undefined) layout['icon-rotate'] = props.iconRotate;
        if (props.iconPadding !== undefined) layout['icon-padding'] = props.iconPadding;
        if (props.iconKeepUpright !== undefined) layout['icon-keep-upright'] = props.iconKeepUpright;
        if (props.iconOffset !== undefined) layout['icon-offset'] = props.iconOffset;
        if (props.iconAnchor !== undefined) layout['icon-anchor'] = props.iconAnchor;
        if (props.iconPitchAlignment !== undefined) layout['icon-pitch-alignment'] = props.iconPitchAlignment;
        if (props.textPitchAlignment !== undefined) layout['text-pitch-alignment'] = props.textPitchAlignment;
        if (props.textRotationAlignment !== undefined) layout['text-rotation-alignment'] = props.textRotationAlignment;
        if (props.textField !== null) layout['text-field'] = props.textField;
        if (props.textFont !== undefined) layout['text-font'] = props.textFont;
        if (props.textSize !== undefined) layout['text-size'] = props.textSize;
        if (props.textMaxWidth !== undefined) layout['text-max-width'] = props.textMaxWidth;
        if (props.textLineHeight !== undefined) layout['text-line-height'] = props.textLineHeight;
        if (props.textLetterSpacing !== undefined) layout['text-letter-spacing'] = props.textLetterSpacing;
        if (props.textJustify !== undefined) layout['text-justify'] = props.textJustify;
        if (props.textRadialOffset !== undefined) layout['text-radial-offset'] = props.textRadialOffset;
        if (props.textVariableAnchor !== null) layout['text-variable-anchor'] = props.textVariableAnchor;
        if (props.textAnchor !== undefined) layout['text-anchor'] = props.textAnchor;
        if (props.textMaxAngle !== undefined) layout['text-max-angle'] = props.textMaxAngle;
        if (props.textRotate !== undefined) layout['text-rotate'] = props.textRotate;
        if (props.textPadding !== undefined) layout['text-padding'] = props.textPadding;
        if (props.textKeepUpright !== undefined) layout['text-keep-upright'] = props.textKeepUpright;
        if (props.textTransform !== undefined) layout['text-transform'] = props.textTransform;
        if (props.textOffset !== undefined) layout['text-offset'] = props.textOffset;
        if (props.textAllowOverlap !== undefined) layout['text-allow-overlap'] = props.textAllowOverlap;
        if (props.textIgnorePlacement !== undefined) layout['text-ignore-placement'] = props.textIgnorePlacement;
        if (props.textOptional !== undefined) layout['text-optional'] = props.textOptional;
        if (props.textWritingMode !== null) layout['text-writing-mode'] = props.textWritingMode;
    } else if (type === 'fill-extrusion') {
        // fillExtrusionSortKey è stato rimosso dalle specifiche, quindi non viene impostato qui.
        // Se dovesse essere reintrodotto, andrebbe aggiunto a LayerStyleProperties in types.ts
        // e decommentato/aggiunto qui.
    } else if (type === 'heatmap') {
        // Per le heatmap, la visibilità è gestita a livello di layer, non qui direttamente
        // ma se ci fossero altre proprietà di layout specifiche per heatmap, andrebbero qui.
        // In questo caso, l'istruzione era di leggere props.visibility e aggiungerla.
        // Tuttavia, la visibilità è gestita globalmente più avanti nella logica di sincronizzazione
        // (combinedLayoutProps['visibility'] = fullLayerFromStore.visible ? 'visible' : 'none';)
        // Per coerenza con la richiesta, aggiungiamo comunque la lettura da props se disponibile.
        if (props.visibility !== undefined) { // props qui è currentLayerState
             layout['visibility'] = props.visibility;
        }
    }
    return layout;
};
// Oggetto helper per definire le proprietà valide per ogni tipo di sorgente MapLibre
// Basato sulle interfacce in src/store/types.ts

// Definiamo un'interfaccia per mappare i tipi di sorgente alle loro chiavi valide
interface ValidSourcePropertiesMap {
    vector: Array<keyof VectorSourceSpecification>;
    raster: Array<keyof RasterSourceSpecification>;
    'raster-dem': Array<keyof RasterDemSourceSpecification>;
    geojson: Array<keyof GeoJSONSourceSpecification>;
    video: Array<keyof VideoSourceSpecification>;
    image: Array<keyof ImageSourceSpecification>;
}

const VALID_SOURCE_PROPERTIES: ValidSourcePropertiesMap = {
    vector: ['type', 'url', 'tiles', 'bounds', 'scheme', 'volatile', 'promoteId', 'tileSize', 'attribution', 'minzoom', 'maxzoom'],
    raster: ['type', 'url', 'tiles', 'bounds', 'scheme', 'volatile', 'tileSize', 'attribution', 'minzoom', 'maxzoom'],
    'raster-dem': ['type', 'url', 'tiles', 'bounds', 'scheme', 'volatile', 'tileSize', 'encoding', 'redFactor', 'greenFactor', 'blueFactor', 'baseShift', 'attribution', 'minzoom', 'maxzoom'],
    geojson: ['type', 'data', 'maxzoom', 'buffer', 'filter', 'tolerance', 'cluster', 'clusterRadius', 'clusterMaxZoom', 'clusterMinPoints', 'clusterProperties', 'lineMetrics', 'generateId', 'promoteId', 'attribution', 'minzoom'],
    video: ['type', 'urls', 'coordinates'],
    image: ['type', 'url', 'coordinates'],
};

// Funzione helper per pulire le opzioni della sorgente
function cleanSourceOptions(sourceSpec: SourceSpecificationUnion): Record<string, any> {
    const cleanedOptions: Record<string, any> = {};
    const sourceType = sourceSpec.type;

    // Controlla se sourceType è una chiave valida di VALID_SOURCE_PROPERTIES
    if (!sourceType || !(sourceType in VALID_SOURCE_PROPERTIES)) {
        console.error(`[cleanSourceOptions] Tipo di sorgente non valido o mancante: ${sourceType}`);
        // Ritorna un oggetto base con il tipo, se disponibile, per evitare errori catastrofici
        return { type: sourceType || 'unknown' };
    }

    // Ora TypeScript sa che sourceType è una chiave valida di ValidSourcePropertiesMap
    // e quindi VALID_SOURCE_PROPERTIES[sourceType] è ben definito.
    const validKeys = VALID_SOURCE_PROPERTIES[sourceType];

    for (const key of validKeys) {
        // È necessario un cast a 'any' per sourceSpec qui perché TypeScript non può
        // restringere il tipo di sourceSpec a uno specifico membro dell'unione (es. VectorSourceSpecification)
        // solo basandosi sul valore di 'key' all'interno del loop.
        // Object.prototype.hasOwnProperty.call è una verifica sicura.
        if (Object.prototype.hasOwnProperty.call(sourceSpec, key)) {
            const value = (sourceSpec as any)[key];
            if (value !== undefined) {
                // Le chiavi in validKeys sono già corrette (es. 'minzoom' in lowercase).
                cleanedOptions[key as string] = value; // Cast 'key' a string per l'indice
            }
        }
    }

    // Assicurati che 'type' sia sempre presente se sourceType è valido
    if (!cleanedOptions.type && sourceType && (sourceType in VALID_SOURCE_PROPERTIES)) {
         cleanedOptions.type = sourceType;
    }

    // Rimuovi 'minzoom' specificamente per sorgenti 'geojson'
    // perché MapLibre GL JS non lo supporta a livello di sorgente per GeoJSON,
    // ma solo a livello di layer.
    if (cleanedOptions.type === 'geojson' && cleanedOptions.hasOwnProperty('minzoom')) {
        delete cleanedOptions.minzoom;
    }

    return cleanedOptions;
}

// Duplicato da layerSlice.ts poiché non è esportato
const extractPropertyKeys = (featureCollection: GeoJSON.FeatureCollection): string[] => {
  const allPropertyKeys = new Set<string>();
  featureCollection.features.forEach(feature => {
    if (feature?.properties) {
      Object.keys(feature.properties).forEach(key => {
        allPropertyKeys.add(key);
      });
    }
  });
  return Array.from(allPropertyKeys);
};


/**
 * Sincronizza i layer GeoJSON e Raster tra lo store Zustand e la mappa MapLibre.
 * Gestisce aggiunta, rimozione e aggiornamento di sorgenti e layer, e il loro ordine.
 * @param map L'istanza della mappa MapLibre.
 * @param styleRevision Un contatore che incrementa quando lo stile della mappa cambia, forzando la risincronizzazione.
 */
export function useLayerSynchronization(map: MaplibreMap | null, styleRevision: number) {
    const managedLayerIds = useRef(new Set<string>());
    // Aggiunto useRef per tracciare dettagli layer, incluso sourceId
    const managedLayerDetailsRef = useRef<{ [key: string]: { sourceId?: string; maplibreLayerId?: string } }>({}); // Aggiunto maplibreLayerId
    const lastAppliedSourceSpecsRef = useRef<Record<string, string>>({});
    const firstSymbolIdRef = useRef<string | undefined>(undefined); // Per memorizzare l'ID del primo layer di simboli
    const [symbolLayerIdReady, setSymbolLayerIdReady] = useState(false);

    // Seleziona i dati necessari dallo store FUORI dall'effetto
    // Nota: 'dataLayers' contiene TUTTI i tipi di layer (GeoJSON, Raster).
    // @ts-ignore - Workaround per errore di tipo "Expected 0-1 arguments, but got 2."
    const dataLayers = useGeoData((state: GeoDataState) => state.dataLayers, shallow);
    const layerStyles = useGeoData((state: GeoDataState) => state.layerStyles); // Uso standard qui
    const setLayerAvailablePropertyKeys = useGeoData((state: GeoDataState) => state.setLayerAvailablePropertyKeys);

    const processedSourcesForKeys = useRef(new Set<string>()); // Per tracciare le sorgenti per cui abbiamo già provato a caricare le chiavi

    useEffect(() => {
        if (!map || !map.isStyleLoaded()) {
            if (firstSymbolIdRef.current !== undefined) {
                firstSymbolIdRef.current = undefined;
                setSymbolLayerIdReady(false);
            }
            return;
        }

        const style = map.getStyle();
        let newFirstSymbolId: string | undefined = undefined;
        if (style && style.layers) {
            const firstSymbolLayer = style.layers.find(layer => layer.type === 'symbol');
            if (firstSymbolLayer) {
                newFirstSymbolId = firstSymbolLayer.id;
            }
        }

        if (firstSymbolIdRef.current !== newFirstSymbolId) {
            firstSymbolIdRef.current = newFirstSymbolId;
            // Notifica che l'ID è pronto o è cambiato (anche se diventa undefined)
            setSymbolLayerIdReady(true);
            if (!newFirstSymbolId) {
                console.warn('[useLayerSynchronization] Nessun layer di tipo "symbol" trovato nello stile corrente. I layer con renderBelowLabels verranno aggiunti sopra.');
            }
        } else if (!symbolLayerIdReady && newFirstSymbolId) {
            // Caso in cui l'ID era già corretto ma symbolLayerIdReady era false (es. primo caricamento)
            setSymbolLayerIdReady(true);
        }


    }, [map, styleRevision]); // styleRevision assicura che venga eseguito se lo stile della mappa cambia

    useEffect(() => {
        if (!map || !map.isStyleLoaded() || !symbolLayerIdReady) {
            // Non procedere se la mappa non è pronta o l'ID del layer di simboli non è stato ancora determinato/confermato.
            // Se symbolLayerIdReady è false, significa che firstSymbolIdRef.current potrebbe non essere aggiornato
            // per lo stile corrente, quindi attendiamo.
            return;
        }
        
        const mapInstance = map; // Per evitare problemi di scope nelle callback

        // Helper function to compare cleaned source specifications
        const compareCleanedSourceSpecs = (
            spec1: Record<string, any> | undefined,
            spec2: Record<string, any> | undefined
        ): { dataChanged: boolean; otherPropsChanged: boolean; typeChanged: boolean } => {
            if (!spec1 && !spec2) return { dataChanged: false, otherPropsChanged: false, typeChanged: false };
            if (!spec1 || !spec2) {
                return { dataChanged: true, otherPropsChanged: true, typeChanged: true };
            }

            const typeChanged = spec1.type !== spec2.type;
            if (typeChanged) {
                return { dataChanged: true, otherPropsChanged: true, typeChanged: true };
            }

            const dataChanged = JSON.stringify(spec1.data) !== JSON.stringify(spec2.data);
            let otherPropsChanged = false;

            const allKeys = new Set([...Object.keys(spec1), ...Object.keys(spec2)]);
            for (const key of allKeys) {
                if (key === 'data' || key === 'type') continue;
                if (JSON.stringify(spec1[key]) !== JSON.stringify(spec2[key])) {
                    otherPropsChanged = true;
                    break;
                }
            }
            return { dataChanged, otherPropsChanged, typeChanged };
        };


        // Helper function to recreate a source and its associated layers
        const recreateSourceAndLayers = (
            sourceIdToRecreate: string,
            newSourceSpec: SourceSpecificationUnion
        ): boolean => {
            const layersToRemoveAndReAdd: LayerSpecification[] = [];
            const mapStyle = map.getStyle();

            if (!mapStyle || !mapStyle.layers) {
                return false;
            }

            mapStyle.layers.forEach(layer => {
                // Type guard to ensure 'source' property exists
                if ('source' in layer && layer.source === sourceIdToRecreate) {
                    const fullLayerDef = map.getLayer(layer.id);
                    if (fullLayerDef) {
                        // Ensure we are pushing a valid LayerSpecification that can be re-added
                        // Background layers don't have 'source' and shouldn't be processed here.
                        // Other layer types that have 'source' are fine.
                        // Estrarre solo le proprietà necessarie invece di JSON.stringify sull'intero oggetto MapLibre
                        const { id, type, source, layout, paint, filter, minzoom, maxzoom } = fullLayerDef;
                        const simplifiedLayerDef: LayerSpecification = { id, type } as LayerSpecification;
                        if (source) (simplifiedLayerDef as any).source = source;
                        if (layout) simplifiedLayerDef.layout = JSON.parse(JSON.stringify(layout));
                        if (paint) simplifiedLayerDef.paint = JSON.parse(JSON.stringify(paint));
                        // Cast 'filter' ad any per generalizzare, dato che non tutti i LayerSpecification lo hanno a livello base
                        if (filter) (simplifiedLayerDef as any).filter = JSON.parse(JSON.stringify(filter));
                        if (minzoom !== undefined) simplifiedLayerDef.minzoom = minzoom;
                        if (maxzoom !== undefined) simplifiedLayerDef.maxzoom = maxzoom;
                        // MapLibre può restituire 'sourceLayer' (camelCase) o 'source-layer' (kebab-case)
                        // ma la specifica per addLayer si aspetta 'source-layer'
                        const sourceLayerProperty = (fullLayerDef as any).sourceLayer || (fullLayerDef as any)['source-layer'];
                        if (sourceLayerProperty) (simplifiedLayerDef as any)['source-layer'] = sourceLayerProperty;

                        layersToRemoveAndReAdd.push(simplifiedLayerDef);
                    }
                }
            });

            layersToRemoveAndReAdd.forEach(layer => {
                if (map.getLayer(layer.id)) {
                    try {
                        map.removeLayer(layer.id);
                    } catch (e) {
                    }
                }
            });

            if (map.getSource(sourceIdToRecreate)) {
                try {
                    map.removeSource(sourceIdToRecreate);
                } catch (e) {
                    return false;
                }
            }

            const cleanedNewSourceSpec = cleanSourceOptions(newSourceSpec);
            if (!cleanedNewSourceSpec.type) {
                return false;
            }

            try {
                map.addSource(sourceIdToRecreate, cleanedNewSourceSpec as any);
                // Assicurati che lastAppliedSourceSpecsRef sia aggiornato con la versione pulita e stringata
                lastAppliedSourceSpecsRef.current[sourceIdToRecreate] = JSON.stringify(cleanedNewSourceSpec);
            } catch (e) {
                return false;
            }

            layersToRemoveAndReAdd.forEach(layerSpecFromMap => { // Rinominato per chiarezza
                if (layerSpecFromMap.type !== 'background') {
                    let storeLayerIdToFind = layerSpecFromMap.id;
                    const storeEntry = Object.entries(managedLayerDetailsRef.current).find(
                        ([, details]) => details.maplibreLayerId === layerSpecFromMap.id
                    );
                    if (storeEntry) {
                        storeLayerIdToFind = storeEntry[0];
                    }

                    const fullLayerFromStore = visibleFullLayers.find(fl => fl.id === storeLayerIdToFind) ||
                                               (Array.isArray(dataLayers) ? dataLayers : [])
                                                    .map(l => ({ ...l, ...(layerStyles[l.id] as LayerStyleProperties) }))
                                                    .find(fl => fl.id === storeLayerIdToFind);

                    if (!fullLayerFromStore) {
                        return;
                    }

                    const paintProps = getPaintProperties(fullLayerFromStore);
                    const layoutPropsFromGetter = getLayoutProperties(fullLayerFromStore);

                    const combinedLayoutProps: LayoutProperties = {
                        ...layoutPropsFromGetter,
                        'visibility': fullLayerFromStore.visible ? 'visible' : 'none',
                    };

                    const cleanedPaint = cleanStyleObject(paintProps);
                    const cleanedLayout = cleanStyleObject(combinedLayoutProps);

                    const reAddSpec: LayerSpecification = {
                        id: layerSpecFromMap.id,
                        type: fullLayerFromStore.maplibreLayerType as LayerSpecification['type'],
                        source: sourceIdToRecreate,
                    } as LayerSpecification;

                    if (cleanedPaint) reAddSpec.paint = cleanedPaint;
                    if (cleanedLayout) {
                        reAddSpec.layout = cleanedLayout;
                    } else if (!fullLayerFromStore.visible) { // Se cleanedLayout è undefined e layer non visibile
                        reAddSpec.layout = { 'visibility': 'none' };
                    }
                    // Se cleanedLayout è undefined e layer visibile, il default di MapLibre è 'visible'
         
                    // --- INIZIO LOG PER SOURCE-LAYER (recreateSourceAndLayers) ---
                    const recreateConditionMet = fullLayerFromStore.sourceSpecification?.type === 'vector' &&
                                             fullLayerFromStore.sourceLayer &&
                                             typeof fullLayerFromStore.sourceLayer === 'string' &&
                                             fullLayerFromStore.sourceLayer.length > 0;
                    // --- FINE LOG PER SOURCE-LAYER (recreateSourceAndLayers) ---
        
                    // Modificato per usare fullLayerFromStore.sourceLayer e aggiungere il controllo del tipo di sorgente per 'vector'
                    // La proprietà 'source-layer' viene aggiunta solo se il tipo di sorgente è 'vector'
                    // e fullLayerFromStore.sourceLayer è una stringa valida e non vuota.
                    if (recreateConditionMet) {
                        (reAddSpec as any)['source-layer'] = fullLayerFromStore.sourceLayer;
                    }
                    // Se la condizione sopra non è vera, 'source-layer' non viene aggiunto a reAddSpec da questa logica.
                    // Questo è in linea con l'esempio fornito, che implica un'aggiunta condizionale
                    // piuttosto che un fallback complesso.
         
                    if (typeof fullLayerFromStore.minzoom === 'number' && !isNaN(fullLayerFromStore.minzoom)) {
                        reAddSpec.minzoom = fullLayerFromStore.minzoom;
                    }
                    if (typeof fullLayerFromStore.maxzoom === 'number' && !isNaN(fullLayerFromStore.maxzoom)) {
                        reAddSpec.maxzoom = fullLayerFromStore.maxzoom;
                    }
                    if (fullLayerFromStore.filter) {
                        (reAddSpec as any).filter = fullLayerFromStore.filter;
                    }

                    if ((reAddSpec as any).metadata) {
                        delete (reAddSpec as any).metadata;
                    }

                    // --- LOG DEFINIZIONE FINALE (recreateSourceAndLayers) ---
                    // --- FINE LOG DEFINIZIONE FINALE ---
                    try {
                        // Tutti i layer vengono aggiunti sotto le etichette, se firstSymbolIdRef.current è disponibile
                        map.addLayer(reAddSpec as Exclude<LayerSpecification, { type: 'background' }>, firstSymbolIdRef.current);
                    } catch (e) {
                    }
                }
            });
            return true;
        };

        // Mappa per controllo rapido esistenza layer nello store (indipendentemente da visibilità)
        const allLayerIdsInStore = new Set(Array.isArray(dataLayers) ? dataLayers.map((l: LayerBase) => l.id) : []);

        // Ricostruisci fullLayers (solo visibili e con stile) per aggiunta/aggiornamento
        const visibleFullLayers = Array.isArray(dataLayers)
            ? dataLayers
                .filter((layer: LayerBase) => layer.visible && layerStyles[layer.id])
                .map((layer: LayerBase) => ({
                    ...layer,
                    ...(layerStyles[layer.id] as LayerStyleProperties)
                }))
            : [];

        const currentMapStyle = map.getStyle();
        if (!currentMapStyle || !currentMapStyle.layers) {
            return;
        }

        const visibleLayersInStoreMap = new Map(visibleFullLayers.map((l: FullLayerData) => [l.id, l]));

        // --- 1. Identifica Layer da Rimuovere o Nascondere ---
        const layerIdsToRemoveCompletely = new Set<string>();
        const layerIdsToHide = new Set<string>();

        managedLayerIds.current.forEach(layerId => {
            const layerStillExistsInStore = allLayerIdsInStore.has(layerId);
            const layerIsVisibleInStore = visibleLayersInStoreMap.has(layerId);

            if (!layerStillExistsInStore) {
                layerIdsToRemoveCompletely.add(layerId);
            } else if (!layerIsVisibleInStore) {
                layerIdsToHide.add(layerId);
            }
        });

        // --- Esegui Rimozioni Complete (Layer + Source) ---
        layerIdsToRemoveCompletely.forEach(layerId => {
            const layerDetails = managedLayerDetailsRef.current[layerId];
            const layerIdToRemoveOnMap = layerDetails?.maplibreLayerId || layerId;
            const sourceIdToRemove = layerDetails?.sourceId || layerId;

            const layerOnMap = map.getLayer(layerIdToRemoveOnMap);
            if (layerOnMap) {
                try { map.removeLayer(layerIdToRemoveOnMap); } catch (e) { /* console.warn(`Errore rimozione layer MapLibre ${layerIdToRemoveOnMap} (store: ${layerId}):`, e); */ }
            }
            if (map.getSource(sourceIdToRemove)) {
                try { map.removeSource(sourceIdToRemove); } catch (e) { /* console.warn(`Errore rimozione sorgente ${sourceIdToRemove} (store: ${layerId}):`, e); */ }
            }
            managedLayerIds.current.delete(layerId);
            delete managedLayerDetailsRef.current[layerId];
        });

        // --- Esegui Nascondimenti (Solo Layer) ---
        layerIdsToHide.forEach(layerId => {
             const layerDetails = managedLayerDetailsRef.current[layerId];
             const layerIdToHideOnMap = layerDetails?.maplibreLayerId || layerId;
             const layerOnMap = map.getLayer(layerIdToHideOnMap);
             if (layerOnMap) {
                 try { map.setLayoutProperty(layerIdToHideOnMap, 'visibility', 'none'); } catch (e) { /* console.warn(`Errore visibilità 'none' per layer ${layerIdToHideOnMap} (store: ${layerId}):`, e); */ }
             }
        });

        // --- 2. Aggiungi/Aggiorna Layer Visibili dallo store ---
        visibleFullLayers.forEach((fullLayer: FullLayerData) => {
            // --- LOG OGGETTO appLayer ---
            // --- FINE LOG OGGETTO appLayer ---
            const { id, visible, maplibreLayerType, maplibreSourceId, maplibreLayerId: trackedMaplibreLayerId } = fullLayer;
 
            const sourceId = maplibreSourceId || id;
            const sourceExists = map.getSource(sourceId);
            const layerIdOnMap = trackedMaplibreLayerId || id;
            let layerExists = map.getLayer(layerIdOnMap); // Use let as it might change

            const currentSourceSpec = fullLayer.sourceSpecification;
            const cleanedCurrentSourceSpecForComparison = currentSourceSpec ? cleanSourceOptions(currentSourceSpec) : undefined;
            const currentSourceSpecString = cleanedCurrentSourceSpecForComparison ? JSON.stringify(cleanedCurrentSourceSpecForComparison) : undefined;

            // --- Logica di gestione chiavi per GeoJSON da URL ---
            // Questa logica ora viene eseguita per ogni layer GeoJSON da URL,
            // sia che la sorgente esista già o venga creata.
            if (cleanedCurrentSourceSpecForComparison?.type === 'geojson' &&
                typeof cleanedCurrentSourceSpecForComparison?.data === 'string' &&
                !processedSourcesForKeys.current.has(sourceId) &&
                mapInstance) { // Assicurati che mapInstance sia disponibile

                const handleSourceData = (e: MapSourceDataEvent) => {
                    if (e.sourceId === sourceId && e.isSourceLoaded && e.sourceDataType !== 'metadata' && !e.tile) {
                        if (mapInstance.getSource(sourceId)) {
                            const features = mapInstance.querySourceFeatures(sourceId);
                            if (features && features.length > 0) {
                                const featureCollection: GeoJSON.FeatureCollection = {
                                    type: 'FeatureCollection',
                                    features: features.map(f => ({
                                        type: 'Feature',
                                        geometry: f.geometry,
                                        properties: f.properties,
                                        id: f.id
                                    }))
                                };
                                const keys = extractPropertyKeys(featureCollection);
                                if (keys.length > 0) {
                                    setLayerAvailablePropertyKeys({ layerId: id, keys });
                                }
                            }
                            processedSourcesForKeys.current.add(sourceId);
                            mapInstance.off('sourcedata', handleSourceData);
                        } else {
                            mapInstance.off('sourcedata', handleSourceData);
                        }
                    }
                };
                mapInstance.on('sourcedata', handleSourceData);
            }
            // --- Fine Logica di gestione chiavi ---


            // --- Logica di Aggiunta/Aggiornamento Sorgente/Layer ---
            if (sourceExists) {
                // --- Source Esiste ---
                let sourceWasRecreated = false;
                if (currentSourceSpec && currentSourceSpecString && cleanedCurrentSourceSpecForComparison) {
                    const prevSourceSpecString = lastAppliedSourceSpecsRef.current[sourceId];
                    const prevSourceSpecParsed = prevSourceSpecString ? JSON.parse(prevSourceSpecString) : undefined;

                    if (prevSourceSpecString !== currentSourceSpecString) {
                        
                        const comparisonResult = compareCleanedSourceSpecs(cleanedCurrentSourceSpecForComparison, prevSourceSpecParsed);
                        let needsRecreation = false;
                        let geoJsonDataOnlyUpdate = false;

                        if (comparisonResult.typeChanged) {
                            needsRecreation = true;
                        } else if (cleanedCurrentSourceSpecForComparison.type === 'geojson') {
                            if (comparisonResult.dataChanged && !comparisonResult.otherPropsChanged) {
                                geoJsonDataOnlyUpdate = true;
                            } else if (comparisonResult.otherPropsChanged || comparisonResult.dataChanged) {
                                needsRecreation = true;
                            }
                        } else { // Not geojson type
                            if (comparisonResult.dataChanged || comparisonResult.otherPropsChanged) {
                                needsRecreation = true;
                            }
                        }

                        if (needsRecreation) {
                            if (recreateSourceAndLayers(sourceId, currentSourceSpec)) {
                                sourceWasRecreated = true;
                                // lastAppliedSourceSpecsRef is updated within recreateSourceAndLayers
                                layerExists = map.getLayer(layerIdOnMap); // Re-check if this specific layer exists
                            } else {
                                return; // Skip to next fullLayer by returning from the forEach callback
                            }
                        } else if (geoJsonDataOnlyUpdate && currentSourceSpec.type === 'geojson') { // Ensure currentSourceSpec is geojson
                            const source = map.getSource(sourceId) as GeoJSONSource | undefined;
                            if (source && typeof source.setData === 'function') {
                                try {
                                    source.setData(currentSourceSpec.data as GeoJSON.FeatureCollection | GeoJSON.Feature | string);
                                    // currentSourceSpecString è già basato su cleanedCurrentSourceSpecForComparison
                                    lastAppliedSourceSpecsRef.current[sourceId] = currentSourceSpecString!;
                                } catch (e) {
                                }
                            }
                        }
                    }
                } // end if (currentSourceSpec && currentSourceSpecString)

                // If layer does not exist (and source wasn't just recreated, which would have handled layers)
                // or if layer exists (it might have been recreated or was already there)
                if (!layerExists && !sourceWasRecreated) {
                     // --- Source Exists, Layer No: Add Layer ---
                    let layerSpecReAdd: LayerSpecification | undefined = undefined;
                    try {
                        const paintProps = getPaintProperties(fullLayer);
                        const layoutPropsFromGetter = getLayoutProperties(fullLayer);

                        const combinedLayoutProps: LayoutProperties = {
                            ...layoutPropsFromGetter,
                            'visibility': fullLayer.visible ? 'visible' : 'none', // Usa fullLayer.visible
                        };

                        const cleanedPaint = cleanStyleObject(paintProps);
                        const cleanedLayout = cleanStyleObject(combinedLayoutProps);

                        layerSpecReAdd = { // Assegnazione qui
                            id: layerIdOnMap,
                            type: maplibreLayerType as LayerSpecification['type'],
                            source: sourceId,
                        } as LayerSpecification;

                        if (cleanedPaint) layerSpecReAdd.paint = cleanedPaint;
                        if (cleanedLayout) {
                            layerSpecReAdd.layout = cleanedLayout;
                        } else if (!fullLayer.visible) { // Se cleanedLayout è undefined e layer non visibile
                            layerSpecReAdd.layout = { 'visibility': 'none' };
                        }
                        // Se cleanedLayout è undefined e layer visibile, il default di MapLibre è 'visible'
    
                        // --- INIZIO LOG PER SOURCE-LAYER (Source Exists, Layer No) ---
                        const srcExistsLayerNoConditionMet = fullLayer.sourceSpecification?.type === 'vector' &&
                                                         fullLayer.sourceLayer &&
                                                         typeof fullLayer.sourceLayer === 'string' &&
                                                         fullLayer.sourceLayer.length > 0;
                        // --- FINE LOG PER SOURCE-LAYER (Source Exists, Layer No) ---

                        // Modificato per usare fullLayer.sourceLayer e aggiungere il controllo del tipo di sorgente per 'vector'
                        // La proprietà 'source-layer' viene aggiunta solo se il tipo di sorgente è 'vector'
                        // e fullLayer.sourceLayer è una stringa valida e non vuota.
                        // fullLayer.sourceSpecification è usato per il controllo del tipo.
                        if (srcExistsLayerNoConditionMet) {
                            (layerSpecReAdd as any)['source-layer'] = fullLayer.sourceLayer;
                        }
                        // Se la condizione sopra non è vera, 'source-layer' non viene aggiunto a layerSpecReAdd da questa logica.
                        if (typeof fullLayer.minzoom === 'number' && !isNaN(fullLayer.minzoom)) {
                            layerSpecReAdd.minzoom = fullLayer.minzoom;
                        }
                        if (typeof fullLayer.maxzoom === 'number' && !isNaN(fullLayer.maxzoom)) {
                            layerSpecReAdd.maxzoom = fullLayer.maxzoom;
                        }
                        if (fullLayer.filter) {
                            (layerSpecReAdd as any).filter = fullLayer.filter;
                        }
                        
                        // --- LOG DEFINIZIONE FINALE (Source Exists, Layer No) ---
                        // --- FINE LOG DEFINIZIONE FINALE ---
                        // Tutti i layer vengono aggiunti sotto le etichette, se firstSymbolIdRef.current è disponibile
                        map.addLayer(layerSpecReAdd, firstSymbolIdRef.current);
                        managedLayerIds.current.add(id);
                        if (!managedLayerDetailsRef.current[id]) {
                           managedLayerDetailsRef.current[id] = { sourceId: sourceId, maplibreLayerId: layerIdOnMap };
                        }
                    } catch (e) { /* console.error(`Error re-adding layer ${layerIdOnMap} (store ID: ${id}) for source ${sourceId}:`, e, "Spec:", layerSpecReAdd); */ }
                } else if (layerExists) { // Layer exists (or was just recreated)
                    // --- Source Esiste, Layer Esiste: Aggiorna Layer ---
                    const latestLayerState = fullLayer;
                    const currentLayerIdOnMap = layerIdOnMap;

                    // 1. Update Visibility
                    const currentVisibility = map.getLayoutProperty(currentLayerIdOnMap, 'visibility');
                    const newVisibility = latestLayerState.visible ? 'visible' : 'none';
                    if (currentVisibility !== newVisibility) {
                        try { map.setLayoutProperty(currentLayerIdOnMap, 'visibility', newVisibility); } catch (error) { /* console.error(`[SyncHook] ERROR during map.setLayoutProperty for ${currentLayerIdOnMap}:`, error); */ }
                    }

                    // 2. Update Paint Properties
                    const paintUpdates = getPaintProperties(latestLayerState);
                    Object.keys(paintUpdates).forEach(key => {
                        try {
                            const currentPaintValue = map.getPaintProperty(currentLayerIdOnMap, key);
                            const changed = typeof paintUpdates[key] === 'object' ?
                                            JSON.stringify(currentPaintValue) !== JSON.stringify(paintUpdates[key]) :
                                            currentPaintValue !== paintUpdates[key];
                            if (changed) { map.setPaintProperty(currentLayerIdOnMap, key, paintUpdates[key]); }
                        } catch (error) { /* Ignore paint property error */ }
                    });

                    // 3. Update Layout Properties (excluding visibility)
                    const layoutUpdates = getLayoutProperties(latestLayerState);
                     Object.keys(layoutUpdates).forEach(key => {
                        try {
                            const currentLayoutValue = map.getLayoutProperty(currentLayerIdOnMap, key);
                            const changed = typeof layoutUpdates[key] === 'object' ?
                                            JSON.stringify(currentLayoutValue) !== JSON.stringify(layoutUpdates[key]) :
                                            currentLayoutValue !== layoutUpdates[key];
                            if (changed) { map.setLayoutProperty(currentLayerIdOnMap, key, layoutUpdates[key]); }
                        } catch (error) { /* Ignore layout property error */ }
                    });

                    // 4. Update Zoom Range
                    const currentMapLayer = map.getLayer(currentLayerIdOnMap); // Renamed to avoid conflict
                    if (currentMapLayer) {
                        const currentMinZoom = currentMapLayer.minzoom;
                        const currentMaxZoom = currentMapLayer.maxzoom;
                        const newMinZoom = latestLayerState.minzoom;
                        const newMaxZoom = latestLayerState.maxzoom;

                        if (currentMinZoom !== newMinZoom || currentMaxZoom !== newMaxZoom) {
                            try {
                                // @ts-ignore
                                map.setLayerZoomRange(currentLayerIdOnMap, newMinZoom ?? null, newMaxZoom ?? null);
                            } catch (error) {
                                /* console.error(`[SyncHook] ERROR during map.setLayerZoomRange for ${currentLayerIdOnMap}:`, error); */
                            }
                        }
                    }
                    // GeoJSON data update is handled above if it's the only change.
                    // No need for the block at 488-503 here.

                    // 6. Update Filter
                    const currentFilter = map.getFilter(currentLayerIdOnMap);
                    const newFilter = latestLayerState.filter;
                    if (JSON.stringify(currentFilter) !== JSON.stringify(newFilter)) {
                        try {
                            map.setFilter(currentLayerIdOnMap, newFilter as any);
                        } catch (error) {
                            /* console.error(`[SyncHook] ERROR during map.setFilter for ${currentLayerIdOnMap}:`, error); */
                        }
                    }
                }
            } else { // Source DOES NOT Exist
                // --- Aggiungi Nuova Source e Layer ---
                let sourceAddedSuccessfully = false;
                if (currentSourceSpec) { // Use currentSourceSpec from top of loop
                    // Pulisci sourceOptions prima di passarlo a map.addSource
                    // cleanedCurrentSourceSpecForComparison is already the cleaned version
                    const cleanedSourceOptions = cleanedCurrentSourceSpecForComparison;

                    // Verifica che 'type' sia presente dopo la pulizia.
                    if (!cleanedSourceOptions || !cleanedSourceOptions.type) {
                        // console.warn(`[SyncHook] Tentativo di aggiungere sorgente ${sourceId} senza tipo o opzioni pulite valide.`);
                    } else {
                        try {
                            // --- Registrazione PMTiles ---
                            const pmtilesProtocolPrefix = 'pmtiles://';
                            let pmtilesHttpUrl: string | undefined = undefined;

                            if (cleanedSourceOptions.url && typeof cleanedSourceOptions.url === 'string' && cleanedSourceOptions.url.startsWith(pmtilesProtocolPrefix)) {
                                pmtilesHttpUrl = cleanedSourceOptions.url.substring(pmtilesProtocolPrefix.length);
                            } else if (cleanedSourceOptions.tiles && Array.isArray(cleanedSourceOptions.tiles) && cleanedSourceOptions.tiles.length > 0 && typeof cleanedSourceOptions.tiles[0] === 'string' && cleanedSourceOptions.tiles[0].startsWith(pmtilesProtocolPrefix)) {
                                pmtilesHttpUrl = cleanedSourceOptions.tiles[0].substring(pmtilesProtocolPrefix.length);
                            }

                            if (pmtilesHttpUrl) {
                                registerPmtilesInstance(pmtilesHttpUrl);
                            }
                            // --- Fine Registrazione PMTiles ---

                            map.addSource(sourceId, cleanedSourceOptions as any);
                            sourceAddedSuccessfully = true;
                            if (currentSourceSpecString) {
                                // currentSourceSpecString è già basato su cleanedCurrentSourceSpecForComparison
                                lastAppliedSourceSpecsRef.current[sourceId] = currentSourceSpecString;
                                // La logica per registrare il listener sourcedata è stata spostata sopra,
                                // quindi verrà eseguita indipendentemente dal fatto che la sorgente sia nuova o esistente.
                            }
                        } catch (e) {
                            // console.error(`[SyncHook] Errore durante l'aggiunta della sorgente ${sourceId} (store ID: ${id}):`, e, "Opzioni:", cleanedSourceOptions);
                        }
                    }
                } else {
                    // console.warn(`[SyncHook] Tentativo di aggiungere sorgente ${sourceId} (store ID: ${id}) senza sourceSpecification valida.`);
                }

                if (!sourceAddedSuccessfully) {
                    // console.warn(`[SyncHook] Aggiunta sorgente ${sourceId} (store ID: ${id}) fallita. Salto aggiunta layer.`);
                    return; // Non continuare se l'aggiunta della sorgente è fallita
                }
                const actualMaplibreLayerIdToAdd = trackedMaplibreLayerId || id;
                managedLayerDetailsRef.current[id] = { sourceId: sourceId, maplibreLayerId: actualMaplibreLayerIdToAdd };

                // Add the Layer
                let layerSpec: LayerSpecification | undefined = undefined;
                try {
                    const paintProps = getPaintProperties(fullLayer);
                    const layoutPropsFromGetter = getLayoutProperties(fullLayer);

                    const initialLayoutPropsCombined: LayoutProperties = {
                        ...layoutPropsFromGetter,
                        'visibility': visible ? 'visible' : 'none',
                    };

                    const cleanedPaint = cleanStyleObject(paintProps);
                    const cleanedLayout = cleanStyleObject(initialLayoutPropsCombined);

                    layerSpec = { // Assegnazione qui
                        id: actualMaplibreLayerIdToAdd,
                        type: maplibreLayerType as LayerSpecification['type'],
                        source: sourceId,
                    } as LayerSpecification;

                    if (cleanedPaint) layerSpec.paint = cleanedPaint;
                    if (cleanedLayout) {
                        layerSpec.layout = cleanedLayout;
                    } else if (!visible) { // Se cleanedLayout è undefined e layer non visibile
                        layerSpec.layout = { 'visibility': 'none' };
                    }
                    // Se cleanedLayout è undefined e layer visibile, il default di MapLibre è 'visible'


                    // Se cleanedLayout è undefined e layer visibile, il default di MapLibre è 'visible'
 
                    // --- INIZIO LOG PER SOURCE-LAYER (New Source, New Layer) ---
                    const sourceSpecType = fullLayer.sourceSpecification?.type;
                    const sourceLayerValue = fullLayer.sourceLayer;
                    const isSourceLayerString = typeof sourceLayerValue === 'string';
                    const sourceLayerLength = isSourceLayerString ? (sourceLayerValue as string).length : undefined;

                    const newSrcNewLayerConditionMet = sourceSpecType === 'vector' &&
                                                    sourceLayerValue && // Controlla truthiness
                                                    isSourceLayerString &&
                                                    sourceLayerLength !== undefined && sourceLayerLength > 0;
                    // --- FINE LOG PER SOURCE-LAYER (New Source, New Layer) ---

                    // Modificato per usare fullLayer.sourceLayer e aggiungere il controllo del tipo di sorgente per 'vector'
                    // La proprietà 'source-layer' viene aggiunta solo se il tipo di sorgente è 'vector'
                    // e fullLayer.sourceLayer è una stringa valida e non vuota.
                    // fullLayer.sourceSpecification è usato per il controllo del tipo.
                    if (newSrcNewLayerConditionMet) {
                        (layerSpec as any)['source-layer'] = fullLayer.sourceLayer;
                    } else {
                    }
                    // Se la condizione sopra non è vera, 'source-layer' non viene aggiunto a layerSpec da questa logica.
 
                    if (typeof fullLayer.minzoom === 'number' && !isNaN(fullLayer.minzoom)) {
                        layerSpec.minzoom = fullLayer.minzoom;
                    }
                    if (typeof fullLayer.maxzoom === 'number' && !isNaN(fullLayer.maxzoom)) {
                        layerSpec.maxzoom = fullLayer.maxzoom;
                    }
                    if (fullLayer.filter) {
                        (layerSpec as any).filter = fullLayer.filter;
                    }
 
                    // --- LOG DEFINIZIONE FINALE (New Source, New Layer) ---
                    // --- FINE LOG DEFINIZIONE FINALE ---
                    // Tutti i layer vengono aggiunti sotto le etichette, se firstSymbolIdRef.current è disponibile
                    map.addLayer(layerSpec, firstSymbolIdRef.current);
                    managedLayerIds.current.add(id);
                } catch (e) {
                    /* console.error(`Error adding layer ${actualMaplibreLayerIdToAdd} (store ID: ${id}) for source ${sourceId}:`, e, "Spec:", layerSpec); */
                    if (map.getSource(sourceId)) {
                        try { map.removeSource(sourceId); } catch (rmErr) { /* console.error(`Error removing source ${sourceId} after failed addLayer:`, rmErr); */ }
                        delete lastAppliedSourceSpecsRef.current[sourceId]; // Clean up ref if source removal was part of cleanup
                    }
                }
            }
        }); // Fine visibleFullLayers.forEach

        // --- 3. Sincronizza Ordine Layer sulla Mappa ---
        // Tutti i layer visibili devono stare sotto firstSymbolIdRef.current, mantenendo il loro ordine relativo.
        // Iteriamo sull'ordine desiderato dei layer visibili (dall'alto verso il basso nell'UI).
        // L'ultimo layer in questo array (quello più in alto nell'UI) deve essere posizionato
        // immediatamente prima di firstSymbolIdRef.current.
        // Gli altri verranno messi prima di quello che li precede nell'ordine UI.

        // Iteriamo sui layer visibili nell'ordine in cui appaiono nell'UI (dall'alto verso il basso).
        // L'obiettivo è che visibleFullLayers[0] sia il più in alto tra i layer utente (ma sotto le etichette),
        // visibleFullLayers[1] sia sotto di esso, e così via.
        // MapLibre: moveLayer(layerId, beforeId) -> 'layerId' viene messo PRIMA di 'beforeId'.
        // Quindi, per avere l'ordine UI [A, B, C] (A in cima) sotto le etichette (LBL),
        // lo stack MapLibre deve essere [..., C, B, A, LBL, ...altri layer base...]
        // 1. Sposta A prima di LBL. Stack: [..., A, LBL, ...]
        // 2. Sposta B prima di A. Stack: [..., B, A, LBL, ...]
        // 3. Sposta C prima di B. Stack: [..., C, B, A, LBL, ...]

        for (let i = 0; i < visibleFullLayers.length; i++) {
            const layerToMove = visibleFullLayers[i];
            const mapLayerIdToMove = managedLayerDetailsRef.current[layerToMove.id]?.maplibreLayerId;

            if (!mapLayerIdToMove || !map.getLayer(mapLayerIdToMove)) {
                continue;
            }

            let beforeId: string | undefined;
            if (i === 0) {
                // Questo è il layer più in alto nell'UI. Deve essere posizionato prima di firstSymbolIdRef.current.
                beforeId = firstSymbolIdRef.current;
            } else {
                // Questo layer deve essere posizionato prima del layer che lo precede nell'array visibleFullLayers
                // (che è quello che nell'UI sta immediatamente sopra di esso e che è già stato posizionato).
                const layerRenderedImmediatelyAbove = visibleFullLayers[i - 1];
                beforeId = managedLayerDetailsRef.current[layerRenderedImmediatelyAbove.id]?.maplibreLayerId;
            }
            
            // Salvaguardia: se il beforeId (sia esso firstSymbolId o un altro layer utente) non esiste sulla mappa,
            // non possiamo usarlo. Questo potrebbe accadere se firstSymbolIdRef.current è undefined (nessuna etichetta)
            // o se il layer 'before' non è stato ancora aggiunto/trovato per qualche motivo.
            // Se beforeId diventa undefined, map.moveLayer sposterà mapLayerIdToMove in cima allo stack
            // (o in cima al gruppo di layer sotto le etichette, se firstSymbolIdRef.current era l'unico beforeId mancante).
            if (beforeId && !map.getLayer(beforeId)) {
                // Se il target 'beforeId' non esiste, e stiamo cercando di posizionare il primo layer (i === 0)
                // e firstSymbolIdRef.current era il target, allora non c'è un "before" specifico,
                // quindi il layer andrà in cima (comportamento di default di addLayer senza beforeId,
                // o moveLayer con beforeId=undefined).
                // Se era un layer successivo (i > 0) e il layer precedente non è trovato,
                // allora questo layer si comporterà come se fosse il primo del suo "sottogruppo".
                beforeId = undefined;
            }

            try {
                map.moveLayer(mapLayerIdToMove, beforeId);
            } catch (error) {
                if (error instanceof Error && error.message.includes('does not exist')) {
                    try {
                        map.moveLayer(mapLayerIdToMove); // Fallback: sposta in cima (relativamente)
                    } catch (fallbackError) {
                        // console.error(`[SyncOrder] Errore durante il fallback dello spostamento del layer ${mapLayerIdToMove}:`, fallbackError);
                    }
                } else {
                    // console.error(`[SyncOrder] Errore durante lo spostamento del layer ${mapLayerIdToMove} prima di ${beforeId}:`, error);
                }
            }
        }
        // --- Fine Sincronizzazione Ordine Layer ---

    }, [map, dataLayers, layerStyles, styleRevision, symbolLayerIdReady]); // Dipendenze dell'useEffect
}